import { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

/**
 * The onboarding preview panel is hidden below Tailwind's `lg` (1024px) via
 * `lg:block` in `PersonalizationLayout`. Keep this constant in lockstep with
 * that class: the learn-basics `mic-test` / `note-ready` / `note-edit` steps
 * drive that panel, so when it's hidden the flow trims to the compact
 * details-only path (see `getFlowForPlatform`).
 */
const PREVIEW_PANEL_BREAKPOINT = 1024;

const getIsPreviewHidden = (): boolean =>
  typeof window !== 'undefined' && window.innerWidth < PREVIEW_PANEL_BREAKPOINT;

/** True when the viewport is too narrow to show the onboarding preview panel. */
export function useIsOnboardingPreviewHidden(): boolean {
  const [isHidden, setIsHidden] = useState(getIsPreviewHidden);

  const updateSize = useDebouncedCallback(() => {
    setIsHidden(getIsPreviewHidden());
  }, 250);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Reconcile against the current width on mount in case it changed between
    // the initial render and effect (e.g. hydration on a resized window).
    setIsHidden(getIsPreviewHidden());
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [updateSize]);

  return isHidden;
}
